import type { CSSProperties, FC, KeyboardEvent, PropsWithChildren, UIEvent } from 'react';

import { memo, useCallback, useMemo, Children } from 'react';
import isEqual from 'react-fast-compare';

import { CarouselProvider } from './CarouselContext';
import { S } from './styles';
import { useCarouselScroll } from '~/components/shared/core/carousel/hooks/useCarouselScroll';
import ArrowLeftLineIcon from '~/components/shared/core/icons/ArrowLeftLineIcon';
import ArrowRightLineIcon from '~/components/shared/core/icons/ArrowRightLineIcon';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { useRovingFocus } from '~/utilities/hooks/useRovingFocus';

type CarouselScrollProps = {
  id: string;
  visibleItemsOnMobile?: number;
  visibleItemsOnTablet?: number;
  visibleItemsOnDesktop?: number;
  ariaLabel?: string;
  onScroll?: (e: UIEvent) => void;
};

export const MAX_ITEMS_MOBILE = 2;
export const MAX_ITEMS_TABLET = 3;
export const MAX_ITEMS_DESKTOP = 5;
export const MAX_ITEMS_DESKTOP_AAF = 4;

const CarouselScrollComponent: FC<CarouselScrollProps & PropsWithChildren> = ({
  children,
  id = 'carousel',
  visibleItemsOnMobile = MAX_ITEMS_MOBILE,
  visibleItemsOnTablet = MAX_ITEMS_TABLET,
  visibleItemsOnDesktop,
  ariaLabel,
  onScroll,
}) => {
  const { breakpoint } = useMediaQueryContext();
  const { siteType } = useAppContext();

  const itemsCount = Children.count(children);
  const sectionId = `${id}-scroll`;

  const maxVisibleItems = useMemo(() => {
    const defaultDesktop = siteType === SiteType.Aaf ? MAX_ITEMS_DESKTOP_AAF : MAX_ITEMS_DESKTOP;
    const viewportMap = {
      mobile: visibleItemsOnMobile,
      tablet: visibleItemsOnTablet,
      desktop: visibleItemsOnDesktop || defaultDesktop,
    };

    return viewportMap[breakpoint ?? 'desktop'];
  }, [siteType, visibleItemsOnMobile, visibleItemsOnTablet, visibleItemsOnDesktop, breakpoint]);

  const {
    leftHidden,
    rightHidden,
    scrollLeft,
    scrollRight,
    tilesWrapperRef,
    setTileWidth,
    handleScroll,
    carouselRef,
  } = useCarouselScroll({
    itemsCount,
    maxVisibleItems,
  });

  const { handleKeyDown, getItemProps } = useRovingFocus();

  const handleCarouselKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      handleKeyDown(e.nativeEvent);
    },
    [handleKeyDown]
  );

  const handleCarouselScroll = useCallback(
    (e: UIEvent) => {
      handleScroll(e);
      onScroll?.(e);
    },
    [handleScroll, onScroll]
  );

  return (
    <S.Carousel onKeyDown={handleCarouselKeyDown}>
      <S.LeftArrow
        data-testid="carousel-arrow-left"
        disabled={leftHidden}
        aria-hidden
        data-is-hidden={leftHidden}
        tabIndex={-1}
        onClick={scrollLeft}
      >
        <ArrowLeftLineIcon />
      </S.LeftArrow>

      <S.RightArrow
        data-testid="carousel-arrow-right"
        disabled={rightHidden}
        aria-hidden
        data-is-hidden={rightHidden}
        tabIndex={-1}
        onClick={scrollRight}
      >
        <ArrowRightLineIcon />
      </S.RightArrow>

      <S.CarouselScrollWrapper onScroll={handleCarouselScroll} ref={carouselRef}>
        <CarouselProvider setTileWidth={setTileWidth} getItemProps={getItemProps}>
          <S.CarouselScroll
            id={sectionId}
            aria-label={ariaLabel}
            ref={tilesWrapperRef}
            style={
              {
                '--products-count': itemsCount + 1,
                '--max-visible-items': maxVisibleItems,
              } as CSSProperties
            }
          >
            {children}
          </S.CarouselScroll>
        </CarouselProvider>
      </S.CarouselScrollWrapper>
    </S.Carousel>
  );
};

export const CarouselScroll = memo(CarouselScrollComponent, isEqual);
